import React from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import TimeSelector from '@/components/selectors/TimeSelector';
import { DAYS_OF_WEEK_FROM_SUNDAY } from '@/core/constants/daysOfWeek';
import { WcnCheckbox } from '@/components';

interface DayOfWeekPeriod {
    dayOfWeek: number;
    from: string;
    until: string;
    isDataCollectionDisabled: boolean;
}

interface DayOfWeekFromUntilProps {
    daysFromUntil: DayOfWeekPeriod[];
    disabled?: boolean;
    onChange: (periods: DayOfWeekPeriod[]) => void;
}

const toDateTime = (time: string, offsetMinutes: number = 0): DateTime | undefined => {
    const parts = time?.split(':');
    if (!parts || parts.length < 2) return undefined;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return undefined;
    return DateTime.local().set({ hour: h, minute: m }).plus({ minutes: offsetMinutes });
};

const DayOfWeekFromUntil: React.FC<DayOfWeekFromUntilProps> = ({ daysFromUntil, disabled = false, onChange }) => {
    const { t } = useTranslation();

    const handleTimeChange = (index: number, field: 'from' | 'until', value: string) => {
        const updated = [...daysFromUntil];
        updated[index] = { ...updated[index], [field]: value };
        onChange(updated);
    };

    const handleCheckboxChange = (index: number, value: boolean) => {
        const updated = [...daysFromUntil];
        updated[index] = { ...updated[index], isDataCollectionDisabled: value };
        onChange(updated);
    };

    return (
        <Box>
            {daysFromUntil.map((item, index) => (
                <Grid container spacing={4} key={item.dayOfWeek} sx={{ mb: 2, alignItems: 'center' }}>
                    <Grid item xs={12} md={2.4} sx={{ display: 'flex', placeSelf: 'center', height: 40 }}>
                        <Box className={`day-label ${disabled ? 'disabled' : ''}`}>
                            {t(DAYS_OF_WEEK_FROM_SUNDAY[item.dayOfWeek])}
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={2.3}>
                        <TimeSelector
                            value={item.from}
                            onChange={(value) => handleTimeChange(index, 'from', value)}
                            label={t('startTime')}
                            disabled={disabled || item.isDataCollectionDisabled}
                            minutesStep={1}
                            maxTime={toDateTime(item.until, -1)}
                        />
                    </Grid>
                    <Grid item xs={6} md={2.3}>
                        <TimeSelector
                            value={item.until}
                            onChange={(value) => handleTimeChange(index, 'until', value)}
                            label={t('endTime')}
                            disabled={disabled || item.isDataCollectionDisabled}
                            minutesStep={1}
                            minTime={toDateTime(item.from, 1)}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={5}
                        sx={{ display: 'flex', placeSelf: 'flex-end', justifyContent: 'flex-end' }}
                    >
                        <WcnCheckbox
                            size="small"
                            label={t('isDataCollectionDisabled')}
                            checked={item.isDataCollectionDisabled}
                            onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                            disabled={disabled}
                            labelSx={{
                                mr: 0,
                                '& .MuiFormControlLabel-label': {
                                    color: '#000',
                                    fontFamily: 'var(--font-family-medium)'
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            ))}
        </Box>
    );
};

export default DayOfWeekFromUntil;
